import PropTypes from 'prop-types';
import React, { useContext, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { FlowContext } from '../../store/context/ReactFlowContext';
import NodeInputHandler from './NodeInputHandler';
import NodeOutputHandler from './NodeOutputHandler';
import AdditionalParamsDialog from '../../components/dialog/AdditionalParamsDialog';
import NodeInfoDialog from '../../components/dialog/NodeInfoDialog';
import { IconTrash, IconCopy, IconInfoCircle, IconAlertTriangle, IconPencil  } from '@tabler/icons-react';
import NodeInputControlHandler from './NodeInputControlHandler';
import NodeOutputControlHandler from './NodeOutputControlHandler';
import NodeEditModal from '../tools/NodeEditModel';
import { useNavigate } from 'react-router-dom';

interface CanvasNodeProps {
  data: any;
}

const CanvasNode: React.FC<CanvasNodeProps> = ({ data }) => {


  const canvas = useSelector((state: any) => state.canvas);
  const { deleteNode, duplicateNode } = useContext(FlowContext);

  const navigate = useNavigate();
  const [showDialog, setShowDialog] = useState(false);
  const [dialogProps, setDialogProps] = useState({});
  const [showInfoDialog, setShowInfoDialog] = useState(false);
  const [infoDialogProps, setInfoDialogProps] = useState({});
  const [warningMessage, setWarningMessage] = useState('');
  const [open, setOpen] = useState(false);
  const [openToolEditModal, setOpenToolEditModal] = useState(false);

  const handleClose = () => {
    setOpen(false);
  };

  const handleOpen = () => {
    setOpen(true);
  };

  const nodeOutdatedMessage = (oldVersion: string, newVersion: string) =>
    `Node version ${oldVersion} outdated\nUpdate to latest version ${newVersion}`;

  const nodeVersionEmptyMessage = (newVersion: string) =>
    `Node outdated\nUpdate to latest version ${newVersion}`;

  const onDialogClicked = () => {
    const dialogProps = {
      data,
      inputParams: data.inputParams.filter((param: any) => param.additionalParams),
      confirmButtonName: 'Save',
      cancelButtonName: 'Cancel',
    };
    setDialogProps(dialogProps);
    setShowDialog(true);
  };


  useEffect(() => {
    const componentNode = canvas.componentNodes.find((nd: any) => nd.name === data.name);
    if (componentNode) {
      if (!data.version) {
        setWarningMessage(nodeVersionEmptyMessage(componentNode.version));
      } else {
        if (componentNode.version > data.version)
          setWarningMessage(nodeOutdatedMessage(data.version, componentNode.version));
      }
    }
  }, [canvas.componentNodes, data.name, data.version]);


  const editNode = (data) => {
    navigate('/tool-edit', { state: { nodeData: data } });
  };

  return (
    <>
      <div
        className={`relative border p-2 shadow-md transition-colors w-96 rounded-lg bg-white  ${
          data.selected ? 'border-blue-500' : 'border-gray-300'
        }`}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
      >
    {data.id !== 0 && open && (
        <div
          className="absolute right-[-3rem] top-0 p-2 cursor-pointer"
        >
          <div className="flex flex-col items-center">
            <button
              title="Duplicate"
              onClick={() => {
                duplicateNode(data.id);
              }}
              className="p-1 text-gray-500 hover:text-blue-500"
            >
              <IconCopy size={20} />
            </button>

            {data.displayName !== 'Input' && data.displayName !== 'Output' && (
           <> 
            <button
              title="Delete"
              onClick={() => {
                deleteNode(data.id);
              }}
              className="p-1 text-gray-500 hover:text-red-500"
            >
              <IconTrash size={20} />
            </button>


            <button
              title="Edit"
              onClick={() => {
                editNode(data);
              }}
              className="p-1 text-gray-500 hover:text-red-500"
            >
              <IconPencil size={20} />
            </button>
            </>
          )}

            <button
              title="Info"
              onClick={() => {
                setInfoDialogProps({ data });
                setShowInfoDialog(true);
              }}
              className="p-1 text-gray-500 hover:text-yellow-500"
            >
              <IconInfoCircle size={20} />
            </button>
          </div>
        </div>
         )}
        <div className="flex items-center p-2">
        {data?.logoUrl && <div className="w-12 h-12 rounded-full bg-white cursor-pointer mr-4">
           <img
              className="w-full h-full p-1 object-contain"
              src={data?.logoUrl}
              alt=""
            />
         
          </div>
           }
          <div className="flex-grow">
            <div className="font-semibold text-lg flex flex-row">{data.label || data.displayName}</div>
          </div>
          {warningMessage && (
            <div className="ml-auto">
              <button className="p-1">
                <IconAlertTriangle size={20} color="orange" title={warningMessage} />
              </button>
            </div>
          )}
        </div>
        <div className="p-2">
          {data?.inputControls?.map((inputControl: any, index: number) => (
            <NodeInputControlHandler key={index} inputControl={inputControl} data={data} />
          ))}
          {data?.outputControls?.map((outputControl: any, index: number) => (
            <NodeOutputControlHandler key={index} outputControl={outputControl} data={data} />
          ))}
          {data?.controlInputs?.map((inputControl: any, index: number) => (
            <NodeInputControlHandler key={index} inputControl={inputControl} data={data} />
          ))}
          {data?.controlOutputs?.map((outputControl: any, index: number) => (
            <NodeOutputControlHandler key={index} outputControl={outputControl} data={data} />
          ))}
        </div>
        {data?.inputAnchors?.map((inputAnchor: any, index: number) => (
          <NodeInputHandler key={index} inputAnchor={inputAnchor} data={data} />
        ))}
        {data?.inputParams?.map((inputParam: any, index: number) => (
          <NodeInputHandler key={index} inputParam={inputParam} data={data} />
        ))}
        {data?.inputParams?.find((param: any) => param.additionalParams) && (
          <div className="text-center mt-2">
            <button
              className="border rounded-full px-4 py-1 bg-white hover:bg-gray-200"
              onClick={onDialogClicked}
            >
              Additional Parameters
            </button>
          </div>
        )}
        {data?.outputAnchors?.map((outputAnchor: any, index: number) => (
          <NodeOutputHandler key={index} outputAnchor={outputAnchor} data={data} />
        ))}
      </div>
      <AdditionalParamsDialog
        show={showDialog}
        dialogProps={dialogProps}
        onCancel={() => setShowDialog(false)}
      ></AdditionalParamsDialog>
      
      <NodeInfoDialog
        show={showInfoDialog}
        dialogProps={infoDialogProps}
        onCancel={() => setShowInfoDialog(false)}
      ></NodeInfoDialog>

    </>
  );
};

CanvasNode.propTypes = {
  data: PropTypes.object,
};

export default CanvasNode;
